<template>
  <div class="store-admin-view">
    <div class="store-admin-view__content">
      <header class="store-admin-view__header">
        <div>
          <p>Administration</p>
          <h1>{{ activeAdminTab?.label || "Admin" }}</h1>
          <span>{{ activeAdminTab?.description || "Manage platform controls and operational review queues." }}</span>
        </div>
        <hs-badge tone="primary" icon="shield">Admin console</hs-badge>
      </header>

      <hs-tabs :tabs="adminTabs" v-model="activeTab" />

      <div class="store-admin-view__tab-panel">
        <admin-subscriptions-view v-if="activeTab === 'subscriptions'" embedded />
        <admin-email-verification-view v-else-if="activeTab === 'email_verification'" embedded />

        <section v-else-if="activeTab === 'rra_ebm_requests'" class="store-admin-view__section">
          <h2>RRA EBM Requests</h2>
          <view-rra-ebm-requests />
        </section>

        <section v-else-if="activeTab === 'store_details_requests'" class="store-admin-view__section">
          <h2>Store Details Requests</h2>
          <view-store-details-requests />
        </section>

        <admin-jack-view v-else-if="activeTab === 'jack_access'" embedded />
        <admin-releases-view v-else-if="activeTab === 'releases'" embedded />

        <section v-else-if="activeTab === 'request_logs'" class="store-admin-view__section">
          <h2>Request Logs</h2>
          <view-request-logs />
        </section>

        <admin-groups-view v-else-if="activeTab === 'groups'" embedded />
      </div>
    </div>
  </div>
</template>

<script>
import ViewRraEbmRequests from "@/components/settings/requests/ViewRraEbmRequests.vue";
import ViewStoreDetailsRequests from "@/components/settings/requests/ViewStoreDetailsRequests.vue";
import AdminJackView from "@/views/dashboard/AdminJackView.vue";
import AdminSubscriptionsView from "@/views/dashboard/AdminSubscriptionsView.vue";
import AdminEmailVerificationView from "@/views/dashboard/AdminEmailVerificationView.vue";
import AdminReleasesView from "@/views/dashboard/AdminReleasesView.vue";
import ViewRequestLogs from "@/components/admin/ViewRequestLogs.vue";
import AdminGroupsView from "@/views/dashboard/AdminGroupsView.vue";
import { HsBadge, HsTabs } from "@/components/ui";

export default {
  name: "StoreAdminView",
  components: {
    ViewRraEbmRequests,
    ViewStoreDetailsRequests,
    AdminJackView,
    AdminSubscriptionsView,
    AdminEmailVerificationView,
    AdminReleasesView,
    ViewRequestLogs,
    AdminGroupsView,
    HsBadge,
    HsTabs,
  },
  data() {
    return {
      activeTab: "subscriptions",
      adminTabs: [
        { id: "subscriptions", label: "Subscriptions", icon: "credit-card", description: "Manage plans, assignments, feature checks, and subscription status." },
        { id: "email_verification", label: "Client Management", icon: "mail", description: "Review user and store email verification from one workspace." },
        { id: "rra_ebm_requests", label: "RRA EBM Requests", icon: "file-text", description: "Approve or reject RRA EBM configuration change requests." },
        { id: "store_details_requests", label: "Store Details Requests", icon: "edit-3", description: "Review store profile change requests and supporting documents." },
        { id: "jack_access", label: "Jack Access", icon: "shield", description: "Issue and audit temporary support access for stores." },
        { id: "releases", label: "Releases", icon: "upload-cloud", description: "Publish app releases and track device version rollout." },
        { id: "request_logs", label: "Request Logs", icon: "activity", description: "Inspect API request logs and operational traces." },
        { id: "groups", label: "Groups", icon: "users", description: "Create, review, and maintain group entities." },
      ],
    };
  },
  computed: {
    activeAdminTab() {
      return this.adminTabs.find((tab) => tab.id === this.activeTab);
    },
  },
};
</script>

<style scoped>
.store-admin-view__content {
  display: grid;
  gap: 1rem;
}

.store-admin-view__header {
  align-items: center;
  background: color-mix(in srgb, var(--hs-surface, #fff) 94%, transparent);
  border: 1px solid var(--hs-border, rgba(148, 163, 184, 0.22));
  border-radius: 14px;
  display: flex;
  gap: 1rem;
  justify-content: space-between;
  padding: 1rem;
}

.store-admin-view__header p {
  color: var(--hs-primary, #3293ec);
  font-size: 0.72rem;
  font-weight: 950;
  letter-spacing: 0.1em;
  margin: 0 0 0.35rem;
  text-transform: uppercase;
}

.store-admin-view__header h1 {
  color: var(--hs-text, #0f172a);
  font-size: 1.4rem;
  font-weight: 950;
  margin: 0;
}

.store-admin-view__header span {
  color: var(--hs-muted, #64748b);
  display: block;
  font-size: 0.88rem;
  font-weight: 700;
  margin-top: 0.35rem;
}

.store-admin-view__tab-panel {
  min-width: 0;
}

.store-admin-view__section {
  background: color-mix(in srgb, var(--hs-surface, #fff) 94%, transparent);
  border: 1px solid var(--hs-border, rgba(148, 163, 184, 0.22));
  border-radius: 14px;
  padding: 1rem;
}

.store-admin-view__section h2 {
  color: var(--hs-text, #0f172a);
  font-size: 1rem;
  font-weight: 950;
  margin: 0 0 1rem;
}

.store-admin-view__tab-panel :deep(.hs-page-backdrop) {
  background: transparent;
  min-height: auto;
}

.store-admin-view__tab-panel :deep(.rounded-xl),
.store-admin-view__tab-panel :deep(.rounded-2xl),
.store-admin-view__tab-panel :deep(.rounded-lg) {
  border-radius: 14px;
  box-shadow: none;
}

.store-admin-view__tab-panel :deep(.bg-white),
.store-admin-view__tab-panel :deep(.dark\:bg-gray-800),
.store-admin-view__tab-panel :deep(.dark\:bg-slate-800) {
  background: color-mix(in srgb, var(--hs-surface, #fff) 94%, transparent);
}

.store-admin-view__tab-panel :deep(table) {
  border-collapse: collapse;
  width: 100%;
}

.store-admin-view__tab-panel :deep(th) {
  color: var(--hs-muted, #64748b);
  font-size: 0.72rem;
  font-weight: 850;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

@media (max-width: 720px) {
  .store-admin-view__header {
    align-items: flex-start;
    flex-direction: column;
  }
}
</style>
